import { Button, Spinner } from "@design";
import { MessageSquareText, Pencil } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import type { FeedbackResponse } from "@/api";
import { useAreaCrumbs } from "@/components/areaCrumbs";
import { EmptyState } from "@/components/build/EmptyState";
import { CenteredScreen } from "@/components/CenteredScreen";
import { PageBreadcrumbs } from "@/components/PageBreadcrumbs";
import { DefinitionList, PageSection } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { formatDateTime } from "@/i18n/format";
import { useAdminCanEdit } from "../useAdminCanEdit";
import { CategoryBadge, StatusBadge, submitterOf } from "./FeedbackBadges";
import { FeedbackScreenshots } from "./FeedbackScreenshots";
import { UpdateStatusModal } from "./UpdateStatusModal";
import { useFeedbackItem } from "./useFeedbackItem";

const LIST_PATH = "/admin/feedback";

function Paragraphs({ text }: Readonly<{ text: string }>) {
  return (
    <Text variant="body-sm" className="whitespace-pre-wrap break-words">
      {text}
    </Text>
  );
}

function FeedbackBody({ item }: Readonly<{ item: FeedbackResponse }>) {
  const { t } = useTranslation("adminFeedback");
  const submitter = submitterOf(item);
  const context = [
    item.pageUrl ? { label: t("detail.pageUrl"), value: <span className="break-all">{item.pageUrl}</span> } : null,
    item.userAgent
      ? { label: t("detail.userAgent"), value: <span className="break-all">{item.userAgent}</span> }
      : null,
  ].filter((entry) => entry !== null);
  const screenshots = item.screenshots ?? [];

  return (
    <div className="flex flex-col gap-10">
      <DefinitionList
        items={[
          { label: t("detail.category"), value: <CategoryBadge category={item.category} /> },
          { label: t("detail.status"), value: <StatusBadge status={item.status} /> },
          {
            label: t("detail.submittedBy"),
            value:
              submitter && item.userEmail && submitter !== item.userEmail
                ? `${submitter} (${item.userEmail})`
                : (submitter ?? t("unknownUser")),
          },
          { label: t("detail.submittedAt"), value: formatDateTime(item.createdAt) },
          ...(item.resolvedAt ? [{ label: t("detail.resolvedAt"), value: formatDateTime(item.resolvedAt) }] : []),
        ]}
      />
      <PageSection title={t("detail.description")}>
        <Paragraphs text={item.description} />
      </PageSection>
      {context.length > 0 && (
        <PageSection title={t("detail.context")}>
          <DefinitionList items={context} />
        </PageSection>
      )}
      {screenshots.length > 0 && (
        <PageSection title={t("detail.screenshots")}>
          <FeedbackScreenshots feedbackUuid={item.uuid} screenshots={screenshots} />
        </PageSection>
      )}
      <PageSection title={t("detail.adminNotes")}>
        {item.adminNotes ? (
          <Paragraphs text={item.adminNotes} />
        ) : (
          <Text variant="body-sm" tone="tertiary">
            {t("detail.noNotes")}
          </Text>
        )}
      </PageSection>
    </div>
  );
}

/** /admin/feedback/:feedbackUuid: one item in full; status + notes editable on larger screens. */
export function FeedbackDetailPage() {
  const { t } = useTranslation("adminFeedback");
  const areas = useAreaCrumbs();
  const canEdit = useAdminCanEdit();
  const { feedbackUuid = "" } = useParams();
  const query = useFeedbackItem(feedbackUuid);
  const [editing, setEditing] = useState(false);
  const item = query.data;

  if (query.isPending) {
    return (
      <CenteredScreen>
        <Spinner size="lg" />
      </CenteredScreen>
    );
  }

  return (
    <div className="flex flex-col">
      <PageBreadcrumbs
        trail={[areas.admin, { label: t("title"), to: LIST_PATH }]}
        current={item?.title ?? ""}
        actions={
          canEdit && item ? (
            <Button variant="secondary" size="sm" leadingIcon={<Pencil size={14} />} onClick={() => setEditing(true)}>
              {t("detail.updateStatus")}
            </Button>
          ) : undefined
        }
      />
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-4 py-6 md:px-8">
        {query.isError && (
          <Text tone="error" role="alert">
            {t("detail.notFound")}
          </Text>
        )}
        {!query.isError && !item && <EmptyState icon={<MessageSquareText size={32} />} title={t("detail.notFound")} />}
        {item && (
          <>
            <Text variant="h2">{item.title}</Text>
            <FeedbackBody item={item} />
            {canEdit && <UpdateStatusModal item={item} open={editing} onClose={() => setEditing(false)} />}
          </>
        )}
      </div>
    </div>
  );
}
